import React, { useCallback, useMemo, useState } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import AddIcon from '@mui/icons-material/Add'
import { usePlacesQuery } from '@cobu/api-client'
import { CobuButton, CobuContainer, CobuStack, CobuText, PlacesListSkeleton } from '@cobu/ui'
import type { Place, PlaceCategory, PlaceSort } from '@cobu/types'
import { RootState } from '../../store'
import { PlaceCard } from '../../components/places/PlaceCard'
import { CATEGORY_ORDER, getCategoryShortLabel } from '../../components/places/categoryMeta'
import { PullToRefreshWrapper } from '../../components/ui/PullToRefreshWrapper'
import { showErrorMessage } from '../../utils/messageToast'
import { Strings } from '../../constants/Strings'

type FilterValue = 'all' | PlaceCategory

const SORT_ORDER: PlaceSort[] = ['mostLiked', 'mostCommented', 'newest', 'oldest']

// Native <select> so category/sort read as OS-native pickers on mobile — the
// pill row overflowed the viewport on narrow screens.
const selectStyle: React.CSSProperties = {
  flexShrink: 0,
  height: 34,
  borderRadius: 999,
  border: 'none',
  background: 'var(--backgroundSecondary)',
  color: 'var(--color)',
  fontSize: 12,
  fontWeight: 600,
  padding: '0 12px',
  cursor: 'pointer',
}

export default function PlacesScreen() {
  const navigate = useNavigate()
  const user = useSelector((state: RootState) => state.auth.user)
  const buildingId = user?.building?.uuid || ''

  const [filter, setFilter] = useState<FilterValue>('all')
  const [sort, setSort] = useState<PlaceSort>('mostLiked')

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
  } = usePlacesQuery(buildingId, {
    category: filter === 'all' ? undefined : filter,
    sort,
  })

  const handleRefresh = useCallback(async () => {
    await refetch()
  }, [refetch])

  const places = useMemo(() => data?.pages.flat() ?? [], [data])

  const strings = Strings.get().places
  const filters: Array<{ value: FilterValue; label: string }> = [
    { value: 'all', label: strings.categories.all },
    ...CATEGORY_ORDER.map((category) => ({ value: category, label: getCategoryShortLabel(category) })),
  ]
  const sortOptions = SORT_ORDER.map((value) => ({ value, label: strings.sort[value] }))

  const itemContent = (_index: number, place: Place) => (
    <PlaceCard place={place} onPress={(p) => navigate(`/places/${p.uuid}`)} />
  )

  const renderContent = () => {
    if (isError) {
      showErrorMessage(strings.genericError)
      return null
    }

    if (isLoading) {
      return <PlacesListSkeleton />
    }

    if (places.length === 0) {
      return (
        <CobuStack ai="center" jc="center" py="$10" px="$4">
          <CobuText variant="body" color="$colorSecondary" ta="center">
            {strings.emptyList}
          </CobuText>
        </CobuStack>
      )
    }

    return (
      <Virtuoso
        data={places}
        itemContent={itemContent}
        computeItemKey={(_index, place) => place.uuid}
        useWindowScroll
        style={{ width: '100%' }}
        endReached={() => {
          if (hasNextPage && !isFetchingNextPage) fetchNextPage()
        }}
        components={{ Footer: () => <div style={{ height: '100px', width: '100%' }} /> }}
      />
    )
  }

  return (
    <PullToRefreshWrapper onRefresh={handleRefresh} isRefreshing={isRefetching} useWindowScroll>
    <CobuContainer flex={1} bg="$background">
      <CobuStack layout="horizontal" ai="center" jc="flex-end" gap="$2" px="$4" pt="$4">
        <CobuButton
          dsVariant="primary"
          h={48}
          onPress={() => navigate('/places/add')}
          iconLeft={<AddIcon htmlColor="white" style={{ fontSize: 18 }} />}
          title={strings.addAPlace}
        />
      </CobuStack>

      <CobuStack layout="horizontal" ai="center" gap="$2" px="$4" py="$3">
        <select
          aria-label={strings.filterAria}
          value={filter}
          onChange={(e) => setFilter(e.target.value as FilterValue)}
          style={selectStyle}
        >
          {filters.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <select
          aria-label={strings.sortAria}
          value={sort}
          onChange={(e) => setSort(e.target.value as PlaceSort)}
          style={selectStyle}
        >
          {sortOptions.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </CobuStack>

      {renderContent()}
    </CobuContainer>
    </PullToRefreshWrapper>
  )
}
